/**
 * Personal Spiritual Journal
 * 
 * A private space for recording insights, reflections, and spiritual experiences
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen, Plus, Trash2, Edit2, Save, X } from 'lucide-react';
import toast from 'react-hot-toast';
import NumerologicalTextReflection from './NumerologicalTextReflection';

interface JournalEntry {
  id: string;
  title: string;
  content: string;
  tags: string[];
  timestamp: number;
  mood?: string;
  spiritualLevel?: number;
}

const PersonalJournal: React.FC = () => {
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [isWriting, setIsWriting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [currentEntry, setCurrentEntry] = useState({
    title: '',
    content: '',
    tags: '',
    mood: '',
    spiritualLevel: 5
  });

  // Load entries from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem('fr00t_journal_entries');
      if (saved) {
        setEntries(JSON.parse(saved));
      }
    } catch (error) {
      console.error('Failed to load journal entries:', error);
    }
  }, []);

  // Save entries to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('fr00t_journal_entries', JSON.stringify(entries));
    } catch (error) {
      console.error('Failed to save journal entries:', error);
    }
  }, [entries]);

  const handleSave = () => {
    if (!currentEntry.title.trim() || !currentEntry.content.trim()) {
      toast.error('Please add a title and content');
      return;
    }

    const entry: JournalEntry = {
      id: editingId || Date.now().toString(),
      title: currentEntry.title,
      content: currentEntry.content,
      tags: currentEntry.tags.split(',').map(t => t.trim()).filter(Boolean),
      timestamp: Date.now(),
      mood: currentEntry.mood,
      spiritualLevel: currentEntry.spiritualLevel
    };

    if (editingId) {
      setEntries(prev => prev.map(e => e.id === editingId ? entry : e));
      toast.success('Journal entry updated!');
    } else {
      setEntries(prev => [entry, ...prev]);
      toast.success('Journal entry saved!');
    }

    // Reset form
    setCurrentEntry({
      title: '',
      content: '',
      tags: '',
      mood: '',
      spiritualLevel: 5
    });
    setIsWriting(false);
    setEditingId(null);
  };

  const handleEdit = (entry: JournalEntry) => {
    setCurrentEntry({
      title: entry.title,
      content: entry.content,
      tags: entry.tags.join(', '),
      mood: entry.mood || '',
      spiritualLevel: entry.spiritualLevel || 5
    });
    setEditingId(entry.id);
    setIsWriting(true);
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this entry?')) {
      setEntries(prev => prev.filter(e => e.id !== id));
      toast.success('Entry deleted');
    }
  };

  const handleCancel = () => {
    setCurrentEntry({
      title: '',
      content: '',
      tags: '',
      mood: '',
      spiritualLevel: 5
    });
    setIsWriting(false);
    setEditingId(null);
  };

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '2rem'
      }}>
        <h2 style={{
          fontSize: '1.5rem',
          fontWeight: '600',
          color: '#8b5cf6',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <BookOpen size={28} />
          Personal Journal
        </h2>

        {!isWriting && (
          <motion.button
            onClick={() => setIsWriting(true)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            style={{
              padding: '10px 16px',
              background: 'rgba(139, 92, 246, 0.2)',
              border: '2px solid #8b5cf6',
              borderRadius: '12px',
              color: '#8b5cf6',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <Plus size={18} />
            New Entry
          </motion.button>
        )}
      </div>

      {/* Writing Form */}
      <AnimatePresence>
        {isWriting && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            style={{
              background: 'rgba(30, 41, 59, 0.9)',
              borderRadius: '16px',
              padding: '1.5rem',
              marginBottom: '2rem',
              border: '2px solid rgba(139, 92, 246, 0.3)'
            }}
          >
            <input
              type="text"
              value={currentEntry.title}
              onChange={(e) => setCurrentEntry(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Entry title..."
              style={{
                width: '100%',
                padding: '12px',
                marginBottom: '1rem',
                background: 'rgba(15, 23, 42, 0.8)',
                border: '2px solid rgba(139, 92, 246, 0.3)',
                borderRadius: '12px',
                color: '#f1f5f9',
                fontSize: '1.125rem',
                fontWeight: '600'
              }}
            />

            <textarea
              value={currentEntry.content}
              onChange={(e) => setCurrentEntry(prev => ({ ...prev, content: e.target.value }))}
              placeholder="Write your thoughts, insights, and reflections..."
              style={{
                width: '100%',
                padding: '12px',
                marginBottom: '1rem',
                background: 'rgba(15, 23, 42, 0.8)',
                border: '2px solid rgba(139, 92, 246, 0.3)',
                borderRadius: '12px',
                color: '#f1f5f9',
                fontSize: '1rem',
                minHeight: '200px',
                resize: 'vertical',
                fontFamily: 'inherit',
                lineHeight: '1.6'
              }}
            />

            <NumerologicalTextReflection text={currentEntry.content} />

            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '1rem',
              marginBottom: '1rem'
            }}>
              <input
                type="text"
                value={currentEntry.tags}
                onChange={(e) => setCurrentEntry(prev => ({ ...prev, tags: e.target.value }))}
                placeholder="Tags (comma separated)"
                style={{
                  padding: '10px',
                  background: 'rgba(15, 23, 42, 0.8)',
                  border: '2px solid rgba(139, 92, 246, 0.3)',
                  borderRadius: '12px',
                  color: '#f1f5f9',
                  fontSize: '0.875rem'
                }}
              />

              <input
                type="text"
                value={currentEntry.mood}
                onChange={(e) => setCurrentEntry(prev => ({ ...prev, mood: e.target.value }))}
                placeholder="Current mood..."
                style={{
                  padding: '10px',
                  background: 'rgba(15, 23, 42, 0.8)',
                  border: '2px solid rgba(139, 92, 246, 0.3)',
                  borderRadius: '12px',
                  color: '#f1f5f9',
                  fontSize: '0.875rem'
                }}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                color: '#8b5cf6',
                fontSize: '0.875rem',
                marginBottom: '0.5rem'
              }}>
                Spiritual Awareness Level: {currentEntry.spiritualLevel}
              </label>
              <input
                type="range"
                min="1"
                max="10"
                value={currentEntry.spiritualLevel}
                onChange={(e) => setCurrentEntry(prev => ({ ...prev, spiritualLevel: parseInt(e.target.value) }))}
                style={{
                  width: '100%'
                }}
              />
            </div>

            <div style={{
              display: 'flex',
              gap: '0.75rem',
              justifyContent: 'flex-end'
            }}>
              <motion.button
                onClick={handleCancel}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                style={{
                  padding: '10px 16px',
                  background: 'rgba(100, 116, 139, 0.2)',
                  border: '2px solid #64748b',
                  borderRadius: '12px',
                  color: '#64748b',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <X size={16} />
                Cancel
              </motion.button>

              <motion.button
                onClick={handleSave}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                style={{
                  padding: '10px 16px',
                  background: 'rgba(139, 92, 246, 0.2)',
                  border: '2px solid #8b5cf6',
                  borderRadius: '12px',
                  color: '#8b5cf6',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <Save size={16} />
                {editingId ? 'Update' : 'Save'} Entry
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Entries List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {entries.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '4rem 2rem',
            color: '#64748b'
          }}>
            <BookOpen size={60} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
            <h3 style={{ marginBottom: '0.5rem', color: '#8b5cf6' }}>
              No journal entries yet
            </h3>
            <p>Create your first entry</p>
          </div>
        ) : (
          entries.map((entry) => (
            <motion.div
              key={entry.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              whileHover={{ scale: 1.01 }}
              style={{
                background: 'rgba(30, 41, 59, 0.8)',
                borderRadius: '12px',
                padding: '1.5rem',
                border: '1px solid rgba(139, 92, 246, 0.2)',
                cursor: 'pointer'
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'start',
                marginBottom: '0.75rem'
              }}>
                <div style={{ flex: 1 }}>
                  <h3 style={{
                    fontSize: '1.125rem',
                    fontWeight: '600',
                    color: '#8b5cf6',
                    marginBottom: '0.5rem'
                  }}>
                    {entry.title}
                  </h3>
                  <div style={{
                    fontSize: '0.75rem',
                    color: '#64748b',
                    display: 'flex',
                    gap: '1rem',
                    flexWrap: 'wrap'
                  }}>
                    <span>{formatDate(entry.timestamp)}</span>
                    {entry.mood && <span>Mood: {entry.mood}</span>}
                    {entry.spiritualLevel && <span>Level: {entry.spiritualLevel}/10</span>}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <motion.button
                    onClick={() => handleEdit(entry)}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    style={{
                      padding: '6px',
                      background: 'rgba(99, 102, 241, 0.2)',
                      border: '1px solid #6366f1',
                      borderRadius: '8px',
                      color: '#6366f1',
                      cursor: 'pointer'
                    }}
                  >
                    <Edit2 size={16} />
                  </motion.button>

                  <motion.button
                    onClick={() => handleDelete(entry.id)}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    style={{
                      padding: '6px',
                      background: 'rgba(239, 68, 68, 0.2)',
                      border: '1px solid #ef4444',
                      borderRadius: '8px',
                      color: '#ef4444',
                      cursor: 'pointer'
                    }}
                  >
                    <Trash2 size={16} />
                  </motion.button>
                </div>
              </div>

              <NumerologicalTextReflection text={entry.content} compact />

              {entry.tags.length > 0 && (
                <div style={{
                  display: 'flex',
                  gap: '0.5rem',
                  flexWrap: 'wrap'
                }}>
                  {entry.tags.map((tag, index) => (
                    <span
                      key={index}
                      style={{
                        padding: '4px 8px',
                        background: 'rgba(139, 92, 246, 0.1)',
                        border: '1px solid rgba(139, 92, 246, 0.2)',
                        borderRadius: '6px',
                        fontSize: '0.75rem',
                        color: '#8b5cf6'
                      }}
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              )}
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
};

export default PersonalJournal;

